import { useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

export type PromptDateMarker = { text: string; display: string; top: number; left: number };

/**
 * Resolved-date markers shared by both composers: a subtle underline floated after each date phrase, with a tooltip
 * "amanhã → terça 7 de julho" that auto-opens on a fresh resolution (`forceTooltipText`) and on hover. The marker
 * coords are viewport-fixed (host measures the caret); positioning matches PromptV2 exactly.
 */
export function PromptDateMarkers({
    markers,
    forceTooltipText,
}: {
    markers: PromptDateMarker[];
    forceTooltipText: string;
}) {
    const theme = useTheme();
    const [hovered, setHovered] = useState<number | null>(null);
    return (
        <>
            {markers.map((m, i) => (
                <Tooltip
                    key={`${i}-${m.text}`}
                    arrow
                    placement="top"
                    open={forceTooltipText === m.text || hovered === i}
                    onOpen={() => setHovered(i)}
                    onClose={() => setHovered((h) => (h === i ? null : h))}
                    slotProps={{ tooltip: { sx: { top: 10, backgroundColor: alpha(theme.palette.background.paper, 0.8) } } }}
                    title={
                        <Typography sx={{ fontSize: 12, whiteSpace: 'nowrap', color: theme.palette.text.secondary }}>
                            {m.text} → <b>{m.display}</b>
                        </Typography>
                    }
                >
                    <Box sx={{
                        position: 'fixed',
                        p: 1,
                        top: m.top+2,
                        left: m.left - (m.text.length * 9),
                        zIndex: theme.zIndex.modal,
                        minHeight: 14,
                        minWidth: (m.text.length * 8.5),
                        borderBottom: `3px solid ${alpha(theme.palette.text.secondary, 0.5)}`,
                    }} />
                </Tooltip>
            ))}
        </>
    );
}
